import classNames from 'classnames'
import {Field} from 'formik'
import _ from 'lodash'
import useStableArray from 'hooks/useStableArray'
import useTranslate from 'modules/local/useTranslate'
import React, {useMemo} from 'react'
import {renderElse, renderOwnChild} from 'views/Shared'
import {defaultInputProps} from './defaultInputProps'
import FieldDecorator, {renderDecoratedLabel, renderDecoratedTitle} from './FieldDecorator'
import {renderIf} from "../../views/Shared";

const defaultFormSchema = [
    {
        title: 'email',
        children: {
            type: 'email',
            ...defaultInputProps.email,
            name: 'email',
        },
    },
    {
        title: 'fullname',
        children: {
            type: 'fullname',
            ...defaultInputProps.fullname,
            name: 'fullname',
        },
    },
    {
        title: 'personal profile',
        children: {
            type: 'text',
            ...defaultInputProps.textArea,
            placeholder: 'brief introduction to yourself',
            name: 'personal_profile',
        },
    },
    {
        title: 'country',
        children: {
            type: 'text',
            ...defaultInputProps.country,
            name: 'country',
        },
    },
    {
        title: 'address',
        children: [
            {
                type: 'province',
                ...defaultInputProps.fullname,
                name: 'province',
            },
            {
                type: 'ward',
                ...defaultInputProps.fullname,
                name: 'ward',
            },
        ],
    },
    {
        title: 'phone',
        children: [
            {
                ...defaultInputProps.prefixPhoneNumber,
                name: 'prefixPhoneNumber',
            },
            {
                ...defaultInputProps.phone,
                name: 'phone_number',
            },
        ],
    },
]

const renderDefaultLabel = (label, params = {}) =>
    renderDecoratedLabel(label, {
        ...params,
    })

export const FieldHeader = ({
                                title,
                                style,
                                children,
                                className,
                                extra = [],
                                renderLabel = renderDecoratedTitle,
                                Wrapper = renderOwnChild,
                            }) => (
    <Wrapper>
        <div
            style={style}
            className={classNames('flex items-end gap-2', className)}>
            {renderLabel(title)}
            <div className="flex-1"/>
            {!_.isEmpty(extra) && (
                <div className="flex gap-2 mb-1">
                    {_.isArray(extra) &&
                    extra.map((item, index) => (
                        <React.Fragment key={index}>{item}</React.Fragment>
                    ))}
                </div>
            )}
        </div>
        {children}
    </Wrapper>
)

const renderChildren = (
    {
        component: C,
        render,
        className,
        label,
        description,
        errors,
        isRequired,
        withProps = {},
        name,
        isInvisble = (values) => false,
        withErrors = (errors) => ({}),
        hideError = false,
        Wrapper = renderOwnChild,
        renderLabel = renderDefaultLabel,
        ...props
    },
    translate
) => (
    <Field name={name}>
        {({
              field,
              form: {touched = {}, errors = {}, ...rest} = {
                  touched: {},
                  errors: {},
              },
          }) =>
            renderElse(
                isInvisble(rest.values),
                <FieldDecorator
                    className={className}
                    hasError={!hideError}
                    key={props.name}
                    {...{
                        ...(_.isEmpty(label)
                            ? {}
                            : {
                                label: renderLabel(label, {
                                    isRequired,
                                    getValue: (name) => _.get(rest.values, name),
                                }),
                            }),
                        ...(_.isEmpty(description)
                            ? {}
                            : {
                                description,
                            }),
                        errors: errors[name],
                        isRequired,
                    }}>
                    <Wrapper>
                        {render ? (
                            render({
                                ...field,
                                errors,
                                touched,
                                form: rest,
                            })
                        ) : (
                            <C
                                {...field}
                                {...withProps}
                                {...withErrors(errors)}
                                {...props}
                                form={rest}
                            />
                        )}
                    </Wrapper>
                </FieldDecorator>
            )
        }
    </Field>
)
const renderChildrens = (children, translate) => {
    return (
        <React.Fragment>
            {children.map((child, i) => (
                <div key={i}>{renderChildren(child, translate)}</div>
            ))}
        </React.Fragment>
    )
}

const renderInlineChildren = (
    children,
    translate,
    className,
    itemClassNames = []
) => {
    const getClassName = (index) => {
        return _.isArray(itemClassNames)
            ? _.get(itemClassNames, `[${index}]`, '')
            : itemClassNames
    }

    return (
        <div className={classNames("w-full", className)}>
            {children.map((child, i) => (
                <div
                    className={getClassName(i)}
                    key={i}>
                    {renderChildren(child, translate)}
                </div>
            ))}
        </div>
    )
}

const RenderWrapper = ({children, ...props}) => (
    <div
        className="w-full flex flex-col gap-1 222-888"
        {...props}>
        {children}
    </div>
)

const FieldsFactory = ({
                           renderTitle,
                           Wrapper = RenderWrapper,
                           formSchema = defaultFormSchema,
                           renderInline = renderInlineChildren,
                       }) => {
    const translate = useTranslate()
    const stableFormSchema = useStableArray(formSchema)

    const renderItemWrapper = ({children}) => children

    const result = useMemo(
        () =>
            stableFormSchema
                .filter((e) => !e?.invisible)
                .map(
                    (
                        {
                            title,
                            children,
                            inline = false,
                            description,
                            desClassName = 'text-xs text-color-400 italic',
                            className,
                            itemClassNames,
                            ItemWrapper = renderItemWrapper,
                        },
                        i
                    ) => {
                        const render = _.isArray(children)
                            ? inline
                                ? renderInline
                                : renderChildrens
                            : renderChildren
                        return (
                            <Wrapper key={i}>
                                {_.isEmpty(title)
                                    ? null
                                    : renderTitle
                                        ? renderTitle(translate(title))
                                        : renderDefaultLabel(translate(title))}
                                <ItemWrapper t={translate}>
                                    {render(children, translate, className, itemClassNames)}
                                </ItemWrapper>
                                {renderIf(
                                    !!description,
                                    <div className={desClassName}>
                                        {translate(description)}
                                    </div>
                                )}
                            </Wrapper>
                        )
                    }
                ),
        [stableFormSchema, renderInline, renderTitle, translate]
    )

    return <React.Fragment>{result}</React.Fragment>
}
export default FieldsFactory
